/**
 * Slide-out drawer that lists historical versions of an article. Clicking
 * a row pulls the full snapshot and renders it read-only in the preview
 * panel below the version list.
 *
 * Versions are pre-update snapshots: v1 = state at creation, v2 = state
 * after first edit, etc. The current live state is NOT in this list —
 * it's whatever the editor has loaded.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import {
  ResearchArticleVersionDetail,
  ResearchArticleVersionSummary,
} from "../../../../models/Research";
import { useLeagueApi } from "../../../../data/LeagueEnvironmentProvider";
import ArticleBody from "../ArticleBody";

/**
 * Small colored chip describing what state a version row was saved in.
 * Mirrors the per-version visibility model on the backend.
 */
function VersionStateBadge({
  status,
  visibility,
}: {
  status: ResearchArticleVersionSummary["status_at_version"];
  visibility: ResearchArticleVersionSummary["visibility_at_version"];
}) {
  let label = "Legacy";
  let bg = "#e0e0e0";
  let fg = "#555";

  if (status === "published") {
    label = "Published";
    bg = "#1b6e2c";
    fg = "#fff";
  } else if (status === "draft" && visibility === "shared") {
    label = "Shared draft";
    bg = "#4c4c4c";
    fg = "#fff";
  } else if (status === "draft" && visibility === "private") {
    label = "Private draft";
    bg = "#f0f0f0";
    fg = "#666";
  }

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 0.75,
        height: 18,
        lineHeight: "18px",
        borderRadius: "3px",
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
        backgroundColor: bg,
        color: fg,
      }}
    >
      {label}
    </Box>
  );
}

interface VersionHistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  slug: string | null;
}

function formatLongDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function VersionHistoryDrawer({
  open,
  onClose,
  slug,
}: VersionHistoryDrawerProps) {
  const api = useLeagueApi();
  const [versions, setVersions] = useState<ResearchArticleVersionSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selected, setSelected] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<ResearchArticleVersionDetail | null>(
    null,
  );
  const [loadingSnap, setLoadingSnap] = useState(false);
  const [snapError, setSnapError] = useState<string | null>(null);

  // Reset when slug or open state changes.
  useEffect(() => {
    if (!open || !slug) {
      setVersions([]);
      setSelected(null);
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setLoadingList(true);
    setListError(null);
    api
      .getResearchArticleVersions(slug)
      .then((resp) => {
        if (cancelled) return;
        setVersions(resp.data.versions ?? []);
        setLoadingList(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load versions",
        );
        setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, open, slug]);

  // Pull snapshot when a version row is clicked.
  useEffect(() => {
    if (selected === null || !slug) {
      setSnapshot(null);
      return;
    }
    let cancelled = false;
    setLoadingSnap(true);
    setSnapError(null);
    setSnapshot(null);
    api
      .getResearchArticleVersion(slug, selected)
      .then((resp) => {
        if (cancelled) return;
        setSnapshot(resp.data);
        setLoadingSnap(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setSnapError(
          err?.response?.data?.error ||
            err?.message ||
            "Failed to load snapshot",
        );
        setLoadingSnap(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, slug, selected]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", md: 520 } } }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          px: 2,
          py: 1.5,
          backgroundColor: "#4c4c4c",
          color: "#fff",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            fontSize: "0.85rem",
            letterSpacing: "0.5px",
          }}
        >
          Version History
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box sx={{ p: 2, overflowY: "auto" }}>
        {loadingList && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {listError && <Alert severity="error">{listError}</Alert>}
        {!loadingList && !listError && versions.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No prior versions yet — every save records one.
          </Typography>
        )}
        {!loadingList && versions.length > 0 && (
          <Stack divider={<Divider flexItem />} spacing={0}>
            {versions.map((v) => (
              <Box
                key={v.id}
                onClick={() => setSelected(v.version_number)}
                sx={{
                  py: 1.25,
                  cursor: "pointer",
                  backgroundColor:
                    selected === v.version_number
                      ? "rgba(207, 10, 44, 0.05)"
                      : "transparent",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.25, flexWrap: "wrap" }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                    {v.version_label}
                  </Typography>
                  <VersionStateBadge
                    status={v.status_at_version}
                    visibility={v.visibility_at_version}
                  />
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                  >
                    {formatLongDateTime(v.edited_at)}
                  </Typography>
                  {v.edited_by?.name && (
                    <Typography
                      sx={{ color: "text.secondary", fontSize: "0.8rem" }}
                    >
                      · {v.edited_by.name}
                    </Typography>
                  )}
                </Stack>
                {v.change_note && (
                  <Typography
                    sx={{ fontSize: "0.85rem", color: "text.primary" }}
                  >
                    {v.change_note}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}

        {selected !== null && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography
              variant="overline"
              sx={{
                display: "block",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: "text.secondary",
                mb: 1,
              }}
            >
              {snapshot?.version_label ?? `v${selected}`} Snapshot
            </Typography>
            {loadingSnap && <CircularProgress size={20} />}
            {snapError && <Alert severity="error">{snapError}</Alert>}
            {snapshot && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {snapshot.snapshot.title}
                </Typography>
                {snapshot.snapshot.summary && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {snapshot.snapshot.summary}
                  </Typography>
                )}
                <ArticleBody markdown={snapshot.snapshot.body_md ?? ""} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}
